import { Observable } from "rxjs";
import { map, catchError, take, debounceTime } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { Resource } from "./resource.model";
import { QueryOptions } from "./query.options";
import { Serializer } from "./serializer.model";
import { environment } from "../../environments/environment";
import { normalizeBaseUrl } from "../../environments/environment.utils";

export class ResourceAdmService<T extends Resource> {
  protected url: string = normalizeBaseUrl(environment.apiAdmin);

  constructor(
    protected httpClient: HttpClient,
    protected endpoint: string,
    protected serializer: Serializer
  ) {}

  public create(item: T): Observable<T> {
    return this.httpClient
      .post<T>(`${this.url}/${this.endpoint}`, this.serializer.toJson(item))
      .pipe(
        map((data) => this.serializer.fromJson(data) as T),
        take(1),
        debounceTime(1000),
        catchError(this.handleError)
      );
  }

  public update(item: T): Observable<T> {
    return this.httpClient
      .put<T>(
        `${this.url}/${this.endpoint}/${item.id}`,
        this.serializer.toJson(item)
      )
      .pipe(
        map((data) => this.serializer.fromJson(data) as T),
        take(1),
        debounceTime(1000),
        catchError(this.handleError)
      );
  }

  public createOrUpdate(item: T): Observable<T> {
    if (!item.id) {
      return this.create(item);
    } else {
      return this.update(item);
    }
  }

  public read(id: number): Observable<T> {
    return this.httpClient.get(`${this.url}/${this.endpoint}/${id}`).pipe(
      map((data: any) => this.serializer.fromJson(data) as T),
      take(1)
    );
  }

  public list(queryOptions: QueryOptions): Observable<T[]> {
    return this.httpClient
      .get(`${this.url}/${this.endpoint}?${queryOptions.toQueryString()}`)
      .pipe(
        map((data: any) => this.convertData(data)),
        take(1)
      );
  }

  public listPageable(queryOptions: QueryOptions): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}/${this.endpoint}/pageable?${queryOptions.toQueryString()}`
      )
      .pipe(take(1));
  }

  public delete(item: T): Observable<T> {
    return this.httpClient.delete<T>(`${this.url}/${this.endpoint}/${item.id}`);
  }

  public consulta(queryOptions: QueryOptions): Observable<any> {
    return this.httpClient
      .get(
        `${this.url}/${this.endpoint}/consulta?${queryOptions.toQueryString()}`
      )
      .pipe(take(1));
  }

  public search(term: string): Observable<T[]> {
    return this.httpClient
      .get(`${this.url}/${this.endpoint}/consulta?${term}`)
      .pipe(
        map((data: any) => {
          return this.convertData(data);
        }),
        take(1)
      );
  }

  public convertData(data: any): T[] {
    return data.map((item) => this.serializer.fromJson(item));
  }

  public get urlEndpoint(): string {
    return `${this.url}/${this.endpoint}`;
  }

  private handleError(error: Response | any) {
    console.error(error.message || error);
    return Observable.throw(error.status);
  }
}
